import {
  addDoc as fbAddDoc,
  deleteDoc as fbDeleteDoc,
  getCountFromServer as fbGetCountFromServer,
  getDocs as fbGetDocs,
  onSnapshot as fbOnSnapshot,
  setDoc as fbSetDoc,
  updateDoc as fbUpdateDoc,
  writeBatch as fbWriteBatch,
  type DocumentData,
  type Firestore,
  type Query,
  type QuerySnapshot,
} from 'firebase/firestore'
import { meter } from './usage.runtime'
import { readSourceFromPath } from './usage'

/**
 * The Firestore calls this app makes, counted on the way through.
 *
 * Firestore tells a client nothing about its own billing, so the only way to watch usage
 * from inside the app is to count what it asks for. Every read of a collection goes through
 * `onSnapshot` or `getDocs` and every change through one of the write helpers, so wrapping
 * those seven functions catches essentially all of it.
 *
 * The wrappers do nothing but add. If the counting ever throws it must not take a listener
 * with it, which is why each one is inside its own try.
 *
 * Import these instead of the Firebase ones in anything that touches data. The functions
 * that only build references — `collection`, `doc`, `query`, `where` — cost nothing and
 * come straight from the SDK.
 *
 * `getCountFromServer` was the exception for a long time and it was the expensive one to
 * miss. An aggregate returns one number, so nothing about it looks like a read, but
 * Firestore bills it per batch of index entries matched — counting `choiceDecisions` was
 * seventeen reads a go. Because it was unwrapped, none of that reached the usage card, and
 * the app's own figures said the Field Guide was nearly free while the console said it was
 * the busiest query in the project. A blind spot in a number somebody is watching for
 * trouble is worse than no number.
 */

const count = (fn: () => void) => { try { fn() } catch { /* never break a read */ } }

/**
 * Which collection a snapshot came from, taken from the documents themselves.
 *
 * The public SDK gives a `Query` no path to ask for — only a `CollectionReference` has one
 * — and reaching into the SDK's internals for it breaks silently on an upgrade. A document
 * knows its own parent, so the first one in the snapshot is asked instead. An empty
 * snapshot has nothing to ask and is billed as a single read, which is why `unknown`
 * costing one read is an acceptable answer.
 */
const sourceOf = (snap: QuerySnapshot<DocumentData> | undefined): string => {
  const first = snap?.docs?.[0]
  return first ? readSourceFromPath(first.ref.parent.path) : 'unknown'
}

/**
 * Which collection a write went to, taken from the reference it was given.
 *
 * A read is attributed from the documents that came back. A write has no snapshot, so the
 * reference is the only thing that knows: `addDoc` is handed a collection, everything else
 * a document, and `type` is what the SDK calls the difference. Anything of another shape is
 * billed to `unknown` rather than dropped, so the breakdown still adds up to the total.
 */
export const writeSourceOf = (ref: unknown): string => {
  const r = ref as { type?: string; path?: string; parent?: { path?: string } } | undefined
  if (r?.type === 'collection' && r.path) return readSourceFromPath(r.path)
  if (r?.type === 'document' && r.parent?.path) return readSourceFromPath(r.parent.path)
  return 'unknown'
}

type OnSnapshot = typeof fbOnSnapshot

type AnyFn = (...args: unknown[]) => unknown

export const onSnapshot: OnSnapshot = ((...args: unknown[]) => {
  const raw = fbOnSnapshot as unknown as AnyFn
  const [ref, next, ...rest] = args
  if (typeof next !== 'function') return raw(...args) as () => void
  const wrapped = (snap: QuerySnapshot<DocumentData>) => {
    // `size` on a query snapshot, and one document for a single-doc listener. This is the
    // number Firestore bills: a snapshot of 364 products is 364 reads.
    count(() => meter.reads(typeof snap?.size === 'number' ? snap.size : 1, sourceOf(snap)))
    return (next as AnyFn)(snap)
  }
  return raw(ref, wrapped, ...rest) as () => void
}) as unknown as OnSnapshot

export const getDocs: typeof fbGetDocs = (async (q: Query<DocumentData>) => {
  const snap = await fbGetDocs(q)
  count(() => meter.reads(snap.size, sourceOf(snap)))
  return snap
}) as typeof fbGetDocs

/**
 * What an aggregate costs: one read per batch of up to a thousand index entries it
 * matched, never fewer than one.
 *
 * So a count over 16,866 documents is seventeen reads to return a single integer, which is
 * the arithmetic that made counting the picklist choices on every page view the most
 * expensive query in the project. Rounding up rather than down keeps this on the same side
 * as the rest of the meter: the figures are meant to read high, because a number watched
 * for trouble should not be the one that understates it.
 */
export const billedForCount = (matched: number): number =>
  Math.max(1, Math.ceil(matched / 1000))

/**
 * Which collection an aggregate counted.
 *
 * A snapshot of documents can be asked what it came from; an aggregate cannot, because it
 * brings back no documents. So the reference it was given is all there is, and only a
 * `CollectionReference` carries a path — a `query(collection(…), where(…))` does not
 * expose one, and reaching into the SDK's internals for it breaks silently on an upgrade.
 * A filtered count is therefore billed to `unknown` rather than dropped, on the same rule
 * the document reads already use: the parts still add up to the whole.
 */
const countSourceOf = (ref: unknown): string => {
  const r = ref as { type?: string; path?: string } | undefined
  return r?.type === 'collection' && r.path ? readSourceFromPath(r.path) : 'unknown'
}

export const getCountFromServer: typeof fbGetCountFromServer = (async (
  q: Parameters<typeof fbGetCountFromServer>[0],
) => {
  const snap = await fbGetCountFromServer(q)
  count(() => meter.reads(billedForCount(snap.data().count), countSourceOf(q)))
  return snap
}) as typeof fbGetCountFromServer

export const addDoc: typeof fbAddDoc = ((...args: Parameters<typeof fbAddDoc>) => {
  count(() => meter.writes(1, writeSourceOf(args[0])))
  return fbAddDoc(...args)
}) as typeof fbAddDoc

export const setDoc: typeof fbSetDoc = ((...args: unknown[]) => {
  count(() => meter.writes(1, writeSourceOf(args[0])))
  return (fbSetDoc as unknown as AnyFn)(...args) as Promise<void>
}) as unknown as typeof fbSetDoc

export const updateDoc: typeof fbUpdateDoc = ((...args: unknown[]) => {
  count(() => meter.writes(1, writeSourceOf(args[0])))
  return (fbUpdateDoc as unknown as AnyFn)(...args) as Promise<void>
}) as unknown as typeof fbUpdateDoc

export const deleteDoc: typeof fbDeleteDoc = ((...args: Parameters<typeof fbDeleteDoc>) => {
  count(() => meter.deletes(1, writeSourceOf(args[0])))
  return fbDeleteDoc(...args)
}) as typeof fbDeleteDoc

/**
 * A batch counts its own operations, because one commit can be four hundred writes and
 * counting the commit as one would understate exactly the case worth watching.
 */
export function writeBatch(db: Firestore) {
  const batch = fbWriteBatch(db)
  return new Proxy(batch, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver)
      if (typeof value !== 'function') return value
      return (...args: unknown[]) => {
        if (prop === 'set' || prop === 'update') count(() => meter.writes(1, writeSourceOf(args[0])))
        if (prop === 'delete') count(() => meter.deletes(1, writeSourceOf(args[0])))
        return (value as (...a: unknown[]) => unknown).apply(target, args)
      }
    },
  })
}
